import { combineEpics } from 'redux-observable';
import { of } from 'rxjs/observable/of';
import { empty } from 'rxjs/observable/empty';
import { catchError } from 'rxjs/operators/catchError';
import { map } from 'rxjs/operators/map';
import { startWith } from 'rxjs/operators/startWith';
import { switchMap } from 'rxjs/operators/switchMap';
import { first } from 'rxjs/operators/first';
import { filter } from 'rxjs/operators/filter';
import { ignoreElements } from 'rxjs/operators/ignoreElements';
import { tap } from 'rxjs/operators/tap';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { TwoFactorAuthActionTypes, confirmMethodRequest, confirmMethodSuccess, confirmMethodFailure, confirmVerificationCodeRequest, confirmVerificationCodeSuccess, confirmVerificationCodeFailure, fetchTwoFactorAuthStatusRequest, fetchTwoFactorAuthStatusSuccess, fetchTwoFactorAuthStatusFailure, turnOffTwoFactorAuthRequest, turnOffTwoFactorAuthSuccess, turnOffTwoFactorAuthFailure, generateTwoFactorAuthBackupCodesRequest, generateTwoFactorAuthBackupCodesSuccess, generateTwoFactorAuthBackupCodesFailure, requestTwoFactorAuthGenerateBackupCodes, fetchSetupPortalTwoFactorStatusFailure, fetchSetupPortalTwoFactorStatusRequest, fetchSetupPortalTwoFactorStatusSuccess, requireUserLogin } from '../actions/TwoFactorAuthActions';
import * as TwoFactorAuthAPI from '../api/TwoFactorAuthAPI';
import * as LoginUrls from '../constants/LoginUrls';
import * as TwoFactorAuthMethods from '../constants/TwoFactorAuthMethods';
import * as TwoFactorAuthPrecedenceTypes from '../constants/TwoFactorAuthPrecedenceTypes';
import { LOGIN_BASE_URL } from '../constants/LoginUrls';
import { fromJSON as transformConfigurationResponse } from '../records/TwoFactorAuthConfigureResponse';
import { fromJSON as transformStatusResponse } from '../records/TwoFactorAuthStatus';
import TwoFactorAuthConfig from '../records/TwoFactorAuthConfig';
import { getBackupCodes, getSetupForLoginPortalId, isSetupForLogin, getShouldNotSetupOnLogin, is2FANudge, getRedirectUrl } from '../selectors/twoFactorAuth';
import { getUserIdFromAuth } from '../selectors/AuthSelector';
import { captureMessage } from '../common/sentry';
import { twoFactorAuthNudgeDismissed, twoFactorAuthNudgeAccomplished } from 'settings-ui-security/lib/tracker';
import LoginAPI from '../api/LoginAPI';
import { trackTwoFactorSetup, TwoFactorAuthTypes, TwoFactorEventActions } from '../lib/tracker';
import { setLSDataForAT } from '../utils/helpers';
import { Metrics } from '../startup/Metrics';
const catchSSoSamlError = () => catchError(err => {
  if (err.status === 403 && err.responseJSON && err.responseJSON.status === 'SSO_NOT_ALLOWED') {
    return of(requireUserLogin());
  } else {
    throw err;
  }
});
const processConfiguration = (request$, context) => {
  return request$.pipe(map(response => {
    setLSDataForAT(response, 'config');
    return transformConfigurationResponse(response);
  }), map(confirmMethodSuccess), catchSSoSamlError(), catchError(err => {
    context.errStatus = err.responseJSON.status;
    return of(confirmMethodFailure(err, context));
  }), startWith(confirmMethodRequest()));
};
const fetchTwoFactorAuthStatus = action$ => action$.ofType(TwoFactorAuthActionTypes.REQUESTED_2FA_STATUS).pipe(switchMap(() => fromPromise(TwoFactorAuthAPI.fetchStatus()).pipe(map(transformStatusResponse), map(fetchTwoFactorAuthStatusSuccess), catchSSoSamlError(), catchError(err => of(fetchTwoFactorAuthStatusFailure(err))), startWith(fetchTwoFactorAuthStatusRequest()))));
const fetchSetupPortalTwoFactorStatus = action$ => action$.ofType(TwoFactorAuthActionTypes.STARTED_SETUP_2FA_FOR_LOGIN).pipe(switchMap(({
  payload
}) => fromPromise(TwoFactorAuthAPI.fetchSetupStatus(payload.portalId)).pipe(map(fetchSetupPortalTwoFactorStatusSuccess), catchSSoSamlError(), catchError(err => of(fetchSetupPortalTwoFactorStatusFailure(err, payload.portalId))), startWith(fetchSetupPortalTwoFactorStatusRequest()))));
const configureGoogleAuth = action$ => action$.ofType(TwoFactorAuthActionTypes.CONFIRMED_2FA_METHOD).pipe(filter(action => {
  return action.payload.method === TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR;
}), switchMap(action => {
  return processConfiguration(fromPromise(TwoFactorAuthAPI.configureGoogleAuth(action.payload.precedence,
  // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
  action.payload.method)), action.payload);
}));
const configureSMS = action$ => action$.ofType(TwoFactorAuthActionTypes.CONFIRMED_SMS_NUMBER).pipe(switchMap(action => {
  return processConfiguration(fromPromise(TwoFactorAuthAPI.configureSMS(action.payload.precedence, action.payload.number)), action.payload);
}));
const confirmVerificationCode = action$ => action$.ofType(TwoFactorAuthActionTypes.CONFIRM_VERIFICATION_CODE_ATTEMPT).pipe(filter(action => action.payload.codeAttempt !== ''), switchMap(({
  payload
}) => fromPromise(TwoFactorAuthAPI.confirmVerificationCode(payload.precedence, payload.codeAttempt, payload.twoFactorConfig)).pipe(
// @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
map(obj => new TwoFactorAuthConfig(obj)), map(confirmVerificationCodeSuccess), catchSSoSamlError(), catchError(err => of(confirmVerificationCodeFailure(err))), startWith(confirmVerificationCodeRequest()))));
const backupCodesIfMissing = (action$, {
  getState
}) => action$.ofType(TwoFactorAuthActionTypes.CONFIRMED_VERIFICATION_CODE_SUCCESS, TwoFactorAuthActionTypes.HUBSPOT_APP_STATUS_SUCCESS).pipe(filter(action => action.type === TwoFactorAuthActionTypes.HUBSPOT_APP_STATUS_SUCCESS ? action.payload === 'CONFIRMED' && getBackupCodes(getState()).size === 0 : getBackupCodes(getState()).size === 0), map(requestTwoFactorAuthGenerateBackupCodes));
const redirectBackToLoginOnLoginSetupCancel = (action$, {
  getState
}) => action$.ofType(TwoFactorAuthActionTypes.CANCELLED_2FA_SETUP, TwoFactorAuthActionTypes.SKIP_2FA_SETUP_NUDGE).pipe(switchMap(() => {
  if (isSetupForLogin(getState())) {
    const redirectUrl = getRedirectUrl(getState());
    if (redirectUrl) {
      /* eslint-disable-next-line promise/catch-or-return, @typescript-eslint/no-floating-promises */
      LoginAPI.validateRedirectUrl(redirectUrl).then(loginValidateRedirectResponse => window.location.href = `${LOGIN_BASE_URL}?loginRedirectUrl=${loginValidateRedirectResponse.loginRedirectUrl}`);
    } else {
      window.location.href = LOGIN_BASE_URL;
    }
  } else if (is2FANudge(getState())) {
    twoFactorAuthNudgeDismissed();
    /* eslint-disable-next-line promise/catch-or-return, @typescript-eslint/no-floating-promises */
    LoginAPI.validateRedirectUrl(getRedirectUrl(getState())).then(loginValidateRedirectResponse => window.location = loginValidateRedirectResponse.loginRedirectUrl);
  }
  return empty();
}));
const track2faComplete = (action$, {
  getState
}) => action$.ofType(TwoFactorAuthActionTypes.CONFIRMED_VERIFICATION_CODE_SUCCESS).pipe(switchMap(() => {
  const method = getState().twoFactorAuth.method;
  switch (method) {
    case TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR:
      trackTwoFactorSetup(TwoFactorAuthTypes.THIRD_PARTY_AUTHENTICATOR, TwoFactorEventActions.ACCOMPLISHED);
      break;
    case TwoFactorAuthMethods.SMS:
      trackTwoFactorSetup(TwoFactorAuthTypes.SMS_CODES, TwoFactorEventActions.ACCOMPLISHED);
      break;
    default:
  }
  return empty();
}));
const redirectBackToHubOnLoginSetupComplete = (action$, {
  getState
}) => action$.ofType(TwoFactorAuthActionTypes.COMPLETED_2FA_SETUP).pipe(switchMap(() => {
  if (isSetupForLogin(getState())) {
    const redirectUrl = getRedirectUrl(getState());
    if (redirectUrl) {
      /* eslint-disable-next-line promise/catch-or-return, @typescript-eslint/no-floating-promises */
      LoginAPI.validateRedirectUrl(redirectUrl).then(loginValidateRedirectResponse => window.location = loginValidateRedirectResponse.loginRedirectUrl);
    } else {
      window.location.href = `${LOGIN_BASE_URL}?loginPortalId=${getSetupForLoginPortalId(getState())}&bypass=false`;
    }
  }
  if (is2FANudge(getState())) {
    twoFactorAuthNudgeAccomplished();
    /* eslint-disable-next-line promise/catch-or-return, @typescript-eslint/no-floating-promises */
    LoginAPI.validateRedirectUrl(getRedirectUrl(getState())).then(loginValidateRedirectResponse => window.location = loginValidateRedirectResponse.loginRedirectUrl);
  }
  return empty();
}));
const turnOffConfig = action$ => action$.ofType(TwoFactorAuthActionTypes.TURNED_OFF_2FA).pipe(switchMap(({
  payload: {
    precedence,
    secondaryEnabled
  }
}) => fromPromise(TwoFactorAuthAPI.deleteConfig(precedence, precedence === TwoFactorAuthPrecedenceTypes.PRIMARY && secondaryEnabled)).pipe(map(transformStatusResponse), map(turnOffTwoFactorAuthSuccess), catchSSoSamlError(), catchError(err => of(turnOffTwoFactorAuthFailure(err, precedence, secondaryEnabled))), startWith(turnOffTwoFactorAuthRequest()))));
const generateBackupCodes = action$ => action$.ofType(TwoFactorAuthActionTypes.REQUESTED_2FA_GENERATE_BACKUP).pipe(switchMap(() => fromPromise(TwoFactorAuthAPI.generateBackupCodes()).pipe(map(generateTwoFactorAuthBackupCodesSuccess), catchSSoSamlError(), catchError(err => of(generateTwoFactorAuthBackupCodesFailure(err))), startWith(generateTwoFactorAuthBackupCodesRequest()))));
const sendSentryForInvalidSetup = (action$, {
  getState
}) => action$.ofType(TwoFactorAuthActionTypes.FETCHED_2FA_STATUS_SUCCESS).pipe(first(), switchMap(() => {
  if (getShouldNotSetupOnLogin(getState())) {
    const userIdHubless = getUserIdFromAuth(getState());
    Metrics.counter('invalid-2fa-setup', {
      user_id_hubless: userIdHubless
    }).increment();
    captureMessage('Someone loaded 2FA setup-on-login after already setting it up', {
      extra: {
        userIdHubless
      }
    });
  }
  return empty();
}));
const redirectToLogin = action$ => action$.ofType(TwoFactorAuthActionTypes.REDIRECT_TO_LOGIN).pipe(tap(() =>
// @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'Location'... Remove this comment to see the full error message
window.location = `${LoginUrls.LOGIN_BASE_URL}?loginRedirectUrl=${encodeURIComponent(window.location.href)}`), ignoreElements());
const updateHubAppTwoFAStatus = action$ => {
  return action$.ofType(TwoFactorAuthActionTypes.HUBSPOT_APP_STATUS_SUCCESS).pipe(filter(action => action.payload === 'CONFIRMED'), switchMap(() => fromPromise(TwoFactorAuthAPI.fetchStatus()).pipe(map(transformStatusResponse), map(fetchTwoFactorAuthStatusSuccess), catchSSoSamlError(), catchError(err => of(fetchTwoFactorAuthStatusFailure(err))), startWith(fetchTwoFactorAuthStatusRequest()))));
};
export default combineEpics(fetchTwoFactorAuthStatus, fetchSetupPortalTwoFactorStatus, configureGoogleAuth, configureSMS, confirmVerificationCode, backupCodesIfMissing, redirectBackToLoginOnLoginSetupCancel, track2faComplete, redirectBackToHubOnLoginSetupComplete, turnOffConfig, generateBackupCodes, sendSentryForInvalidSetup, redirectToLogin, updateHubAppTwoFAStatus);